import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import Header from "@/components/layout/header";
import { useBookings } from "@/hooks/useBookings";
import AdvancedFilters, { type FilterCriteria } from "@/components/ui/AdvancedFilters";
import SearchAndSort, { type SearchSortCriteria, Pagination } from "@/components/ui/SearchAndSort";
import BookingWorkflowProgress, { MiniWorkflowProgress } from "@/components/BookingWorkflowProgress";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { BookingListLoading, ErrorState, EmptyState } from "@/components/ui/LoadingStates";
import {
  BookingSummaryResponse,
  BookingResponseUI,
  BookingStatus,
  transformBookingToUIDisplay,
  mapBookingStatusToUIStatus,
  getBookingWorkflowStep
} from "@/types/activities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { 
  Search,
  Filter,
  Grid,
  List,
  Send,
  MessageSquare,
  Settings,
  Store,
  FileText,
  Calendar,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronRight,
  Plus,
  Download,
  RefreshCw,
  Building2,
  Clock,
  Eye,
  Package
} from "lucide-react";


type BookingFilterType = 'all' | 'marketplace';
type BookingFilterStatus = 'all' | 'pending' | 'negotiating' | 'awaiting-approval' | 'vendor-booking' | 'confirmed' | 'cancelled' | 'no-vendors';
type ViewMode = 'table' | 'card';

// Using the new advanced filter system - old interface removed

const getTypeColor = (type: string) => {
  switch (type) {
    case "marketplace": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "pending": return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:bg-opacity-20 dark:text-blue-400";
    case "negotiating": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:bg-opacity-20 dark:text-yellow-400";
    case "awaiting-approval": return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:bg-opacity-20 dark:text-purple-400";
    case "vendor-booked": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:bg-opacity-20 dark:text-orange-400";
    case "confirmed": return "bg-green-100 text-green-800 dark:bg-green-900 dark:bg-opacity-20 dark:text-green-400";
    case "cancelled": 
    case "rejected": 
    case "expired": return "bg-red-100 text-red-800 dark:bg-red-900 dark:bg-opacity-20 dark:text-red-400";
    default: return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  }
};

export default function BookingManagement() {
  const [location, setLocation] = useLocation();
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('table');

  // Advanced filter criteria
  const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({});

  // Search and sort criteria
  const [searchSortCriteria, setSearchSortCriteria] = useState<SearchSortCriteria>({
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
    itemsPerPage: 25,
    currentPage: 1
  });

  // Fetch bookings using new hook
  const { data: bookingResponses = [], loading: isLoading, error, refetch } = useBookings();

  // Transform booking responses to UI display format
  const bookings = useMemo(() => {
    return bookingResponses.map(booking =>
      transformBookingToUIDisplay(booking)
    );
  }, [bookingResponses]);

  // Advanced filtering and search
  const filteredAndSortedBookings = useMemo(() => {
    let filtered = [...bookings];

    // Apply search filter
    if (searchSortCriteria.search) {
      const searchTerm = searchSortCriteria.search.toLowerCase();
      filtered = filtered.filter(booking =>
        booking.title.toLowerCase().includes(searchTerm) ||
        booking.organizationName.toLowerCase().includes(searchTerm) ||
        booking.vendorName?.toLowerCase().includes(searchTerm) ||
        booking.description?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply advanced filters
    if (filterCriteria.status?.length) {
      filtered = filtered.filter(booking => {
        // Convert booking's raw status to UI status for comparison
        const uiStatus = mapBookingStatusToUIStatus(booking.booking.status);
        return filterCriteria.status!.some(filterStatus => {
          // filterStatus comes from BookingStatus enum, so we need to map it too
          const filterUIStatus = mapBookingStatusToUIStatus(filterStatus);
          return uiStatus === filterUIStatus;
        });
      });
    }

    if (filterCriteria.organizationIds?.length) {
      filtered = filtered.filter(booking =>
        filterCriteria.organizationIds!.includes(booking.organizationId)
      );
    }

    if (filterCriteria.requiresAttention) {
      filtered = filtered.filter(booking =>
        booking.status === 'pending' || booking.status === 'awaiting-approval'
      );
    }

    const dateStart = filterCriteria.dateRange?.start;
    if (dateStart) {
      filtered = filtered.filter(booking =>
        new Date(booking.dateSubmitted) >= dateStart
      );
    }

    const dateEnd = filterCriteria.dateRange?.end;
    if (dateEnd) {
      filtered = filtered.filter(booking =>
        new Date(booking.dateSubmitted) <= dateEnd
      );
    }

    const amountMin = filterCriteria.amountRange?.min;
    if (amountMin) {
      filtered = filtered.filter(booking =>
        (booking.booking.confirmedPrice ?? booking.booking.originalMarketplacePrice ?? 0) >= amountMin
      );
    }

    const amountMax = filterCriteria.amountRange?.max;
    if (amountMax) {
      filtered = filtered.filter(booking =>
        (booking.booking.confirmedPrice ?? booking.booking.originalMarketplacePrice ?? 0) <= amountMax
      );
    }

    const participantMin = filterCriteria.participantRange?.min;
    if (participantMin) {
      filtered = filtered.filter(booking =>
        booking.booking.estimatedParticipantsCount >= participantMin
      );
    }

    const participantMax = filterCriteria.participantRange?.max;
    if (participantMax) {
      filtered = filtered.filter(booking =>
        booking.booking.estimatedParticipantsCount <= participantMax
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const sortBy = searchSortCriteria.sortBy;
      const sortOrder = searchSortCriteria.sortOrder;

      let aValue: any = a[sortBy as keyof BookingResponseUI];
      let bValue: any = b[sortBy as keyof BookingResponseUI];

      // Handle different data types
      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [bookings, filterCriteria, searchSortCriteria]);

  // Pagination
  const paginatedBookings = useMemo(() => {
    const startIndex = (searchSortCriteria.currentPage - 1) * searchSortCriteria.itemsPerPage;
    const endIndex = startIndex + searchSortCriteria.itemsPerPage;
    return filteredAndSortedBookings.slice(startIndex, endIndex);
  }, [filteredAndSortedBookings, searchSortCriteria.currentPage, searchSortCriteria.itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedBookings.length / searchSortCriteria.itemsPerPage);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBookings(paginatedBookings.map(booking => booking.id));
    } else {
      setSelectedBookings([]);
    }
  };

  const handleSelectBooking = (bookingId: string, checked: boolean) => {
    if (checked) {
      setSelectedBookings([...selectedBookings, bookingId]);
    } else {
      setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
    }
  };

  const openBookingDrawer = (booking: BookingResponseUI) => {
    // Navigate to booking detail page instead of opening drawer
    setLocation(`/bookings/${booking.id}`);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header
          title="Booking Management"
          description="Review and manage organization marketplace bookings"
        />
        <div className="flex-1 p-6">
          <BookingListLoading count={8} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <Header
          title="Booking Management"
          description="Review and manage organization marketplace bookings"
        />
        <div className="flex-1 flex items-center justify-center p-6">
          <ErrorState
            title="Failed to load bookings"
            description={error || 'An error occurred while loading bookings. Please try again.'}
            action={{
              label: 'Try Again',
              onClick: () => refetch()
            }}
            className="max-w-md"
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col h-full">
        <Header
          title="Booking Management"
          description="Review and manage organization marketplace bookings"
        />
        
        <div className="flex-1 overflow-auto p-6">
          <ErrorBoundary level="section">
            {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <Card className="athlyn-card">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Bookings</p>
                    <p className="text-2xl font-bold text-gray-900">{filteredAndSortedBookings.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="athlyn-card">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Requiring Attention</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredAndSortedBookings.filter(b => b.status === 'pending' || b.status === 'awaiting-approval').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="athlyn-card">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Pending Contact</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredAndSortedBookings.filter(b => b.status === 'pending').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="athlyn-card">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Confirmed</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {filteredAndSortedBookings.filter(b => b.status === 'confirmed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Advanced Search and Filters */}
          <Card className="athlyn-card mb-6">
            <CardContent className="p-6">
              <div className="space-y-4">
                {/* Advanced Filters */}
                <AdvancedFilters
                  criteria={filterCriteria}
                  onCriteriaChange={setFilterCriteria}
                  organizations={bookings.map(b => ({
                    id: b.organizationId,
                    name: b.organizationName
                  })).filter((org, index, self) =>
                    index === self.findIndex(o => o.id === org.id)
                  )}
                />

                {/* Search and Sort */}
                <SearchAndSort
                  criteria={searchSortCriteria}
                  onCriteriaChange={setSearchSortCriteria}
                  totalItems={filteredAndSortedBookings.length}
                  isLoading={isLoading}
                  showBulkActions={false}
                  selectedItems={selectedBookings}
                  onSelectedItemsChange={setSelectedBookings}
                  onRefresh={refetch}
                />

                {/* View Toggle */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {filteredAndSortedBookings.length} total bookings
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('table')}
                    >
                      <List className="w-4 h-4 mr-1" />
                      Table
                    </Button>
                    <Button
                      variant={viewMode === 'card' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setViewMode('card')}
                    >
                      <Grid className="w-4 h-4 mr-1" />
                      Grid
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bookings Display */}
          {filteredAndSortedBookings.length > 0 ? (
            viewMode === 'table' ? (
              // Table View
              <Card className="athlyn-card">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={paginatedBookings.length > 0 && selectedBookings.length === paginatedBookings.length}
                            onCheckedChange={handleSelectAll}
                            aria-label="Select all bookings"
                          />
                        </TableHead>
                        <TableHead>Organization</TableHead>
                        <TableHead>Activity</TableHead>
                        <TableHead>Service</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Progress</TableHead>
                        <TableHead>Date Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedBookings.map((booking) => (
                        <TableRow
                          key={booking.id}
                          className={`hover:bg-muted hover:bg-opacity-50 transition-colors ${
                            selectedBookings.includes(booking.id) ? 'bg-primary bg-opacity-10' : ''
                          }`}
                        >
                          <TableCell>
                            <Checkbox
                              checked={selectedBookings.includes(booking.id)}
                              onCheckedChange={(checked) => handleSelectBooking(booking.id, !!checked)}
                              aria-label={`Select ${booking.title}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {booking.organizationLogo ? (
                                <img
                                  src={booking.organizationLogo}
                                  alt={booking.organizationName}
                                  className="w-8 h-8 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-primary" />
                                </div>
                              )}
                              <span className="font-medium">{booking.organizationName}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{booking.title}</TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">
                              {booking.vendorPackage}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${getStatusColor(booking.status)} text-xs`}>
                              {booking.status.replace('-', ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="min-w-[200px]">
                            <MiniWorkflowProgress currentStatus={booking.booking.status} />
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(booking.dateSubmitted).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openBookingDrawer(booking)}
                                className="flex items-center gap-1"
                              >
                                <Eye className="w-4 h-4" />
                                Open
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            ) : (
              // Card View
              <div className="grid grid-cols-1 gap-4">
                {paginatedBookings.map((booking) => (
                  <Card
                    key={booking.id}
                    className={`athlyn-card transition-all ${
                      selectedBookings.includes(booking.id) ? 'ring-2 ring-primary' : ''
                    }`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <Checkbox
                          checked={selectedBookings.includes(booking.id)}
                          onCheckedChange={(checked) => handleSelectBooking(booking.id, !!checked)}
                          aria-label={`Select ${booking.title}`}
                        />

                        <div className="flex-1 space-y-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary bg-opacity-10 rounded-lg flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-primary" />
                              </div>
                              <div>
                                <h3 className="font-semibold text-foreground">{booking.title}</h3>
                                <p className="text-sm text-muted-foreground">{booking.organizationName}</p>
                              </div>
                            </div>
                            <Badge variant="outline" className={`${getStatusColor(booking.status)} text-xs`}>
                              {booking.status.replace('-', ' ')}
                            </Badge>
                          </div>

                          <p className="text-sm text-muted-foreground">{booking.description}</p>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Calendar className="w-4 h-4" />
                              {new Date(booking.dateSubmitted).toLocaleDateString()}
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                onClick={() => openBookingDrawer(booking)}
                                className="flex items-center gap-2"
                              >
                                Open
                                <ChevronRight className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <BookingWorkflowProgress
                            currentStatus={booking.booking.status}
                            orientation="horizontal"
                            showDescriptions={false}
                            className="mt-4"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )
          ) : (
            // Empty State
            <EmptyState
              title="No bookings found"
              description="There are no bookings matching your current filters. Try adjusting your search criteria or refresh to see all bookings."
              icon={FileText}
              action={{
                label: 'Refresh',
                onClick: () => refetch()
              }}
              className="athlyn-card"
            />
          )}

          {/* Pagination */}
          {filteredAndSortedBookings.length > 0 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={searchSortCriteria.currentPage}
                totalPages={totalPages}
                onPageChange={(page) => setSearchSortCriteria(prev => ({ ...prev, currentPage: page }))}
                isLoading={isLoading}
              />
            </div>
          )}
          </ErrorBoundary>
        </div>
      </div>
    </>
  );
}